import { motion } from 'framer-motion'
import { usePageMeta } from '@/hooks/usePageMeta'

const ACCENT = '#B9A3D9'
const ACCENT_SOFT = '#d4c5e8'
// Hotmart link — será substituído quando fornecido
const SESSAO_LINK = 'https://pay.hotmart.com/X105734944C'

const PROCESSO_ITEMS = [
  { title: 'Orientação', desc: 'entendemos juntos o padrão que está operando no seu campo e de onde ele vem.' },
  { title: 'Liberação', desc: 'trabalhamos a dissolução do que está prendendo sua energia nessa frequência.' },
  { title: 'Ressignificação', desc: 'criamos um novo significado para o que você viveu, sem negar, sem reprimir.' },
  { title: 'Cura', desc: 'o campo recebe o que precisa para começar a se reorganizar em uma nova vibração.' },
  { title: 'Elevação', desc: 'você sai com clareza, direcionamento e uma frequência diferente da que chegou.' },
]

const RECEBE_ITEMS = [
  { text: 'Sessão individual de 60 minutos com Mônica Maria.' },
  { title: 'Bônus — Plano de Voo', desc: '30 exercícios práticos para reconhecer crenças limitantes, liberar bloqueios emocionais e criar uma vida de riqueza e crescimento.' },
]

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] tracking-[0.4em] uppercase font-body mb-5" style={{ color: ACCENT_SOFT }}>
      {children}
    </p>
  )
}

function Divider() {
  return (
    <div
      className="h-px w-full my-8"
      style={{ background: `linear-gradient(90deg, transparent, ${ACCENT}44, transparent)` }}
    />
  )
}

export default function PageSessao() {
  usePageMeta({
    title: 'Sessão de Liberação e Elevação Vibracional — Reative os 4 Elementos',
    description: 'Atendimento individual e profundo de orientação, liberação, ressignificação, cura e elevação.',
    ogImage: 'https://static.lumi.new/f3/f3946acbbd416420814f7e5245826af0.svg',
    ogUrl: 'https://jornada.reacendaluz.com.br/sessao/',
  })

  return (
    <div className="min-h-screen w-full font-body" style={{ background: '#0a0f14', color: 'var(--ink, #e8e0d5)' }}>

      {/* Header */}
      <header
        className="w-full px-4 py-5 flex items-center justify-center border-b"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <p className="text-[11px] tracking-[0.35em] uppercase font-body opacity-60" style={{ color: ACCENT_SOFT }}>
          Reacenda Luz · Mônica Maria
        </p>
      </header>

      <main className="w-full max-w-3xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >

          {/* ── HERO ── */}
          <div className="text-center mb-14">
            {/* Sigil */}
            <motion.div
              initial={{ opacity: 0, scale: 0.88 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.1 }}
              className="flex justify-center mb-7"
            >
              <svg width="52" height="52" viewBox="0 0 52 52" fill="none" aria-hidden="true">
                <circle cx="26" cy="26" r="22" stroke={ACCENT} strokeWidth="1.2" opacity="0.5" />
                <circle cx="26" cy="26" r="14" stroke={ACCENT_SOFT} strokeWidth="0.7" opacity="0.3" />
                <path d="M26 8 L26 44 M8 26 L44 26" stroke={ACCENT} strokeWidth="0.8" opacity="0.3" />
                <circle cx="26" cy="26" r="3" fill={ACCENT} opacity="0.5" />
              </svg>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-[10px] tracking-[0.4em] uppercase font-body mb-5"
              style={{ color: ACCENT_SOFT }}
            >
              Atendimento Individual · <span className="numerals">60</span> minutos
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.3 }}
              className="font-display type-hero text-[var(--ink,#e8e0d5)] mb-5"
            >
              Sessão de Liberação e{' '}
              <em className="italic font-light" style={{ color: ACCENT_SOFT }}>
                Elevação Vibracional
              </em>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.45 }}
              className="font-display italic text-[18px] md:text-[20px] mb-8 max-w-2xl mx-auto leading-relaxed"
              style={{ color: 'rgba(232,224,213,0.7)' }}
            >
              Um atendimento individual e profundo de orientação, liberação, ressignificação, cura e elevação.
            </motion.p>

            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 80, opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.55 }}
              className="h-px mx-auto"
              style={{ background: `linear-gradient(90deg, transparent, ${ACCENT}88, transparent)` }}
            />
          </div>

          {/* ── TEXTO DE ABERTURA ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6 }}
            className="rounded-sm p-6 sm:p-8 md:p-10 mb-10 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(185,163,217,0.06) 0%, rgba(255,255,255,0.02) 100%)',
              border: `1px solid ${ACCENT}28`,
            }}
          >
            {/* Corner glow */}
            <div
              className="absolute -top-20 -right-20 w-48 h-48 pointer-events-none"
              style={{ background: `radial-gradient(closest-side, ${ACCENT}14, transparent)` }}
            />

            <p className="font-body type-body leading-relaxed mb-5" style={{ color: 'rgba(232,224,213,0.75)' }}>
              Você chegou até aqui porque algo dentro de você sabe que é hora de mudar. Não uma mudança superficial. Uma mudança real. No campo. Na frequência. Na forma como você se vê e cria sua realidade.
            </p>
            <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.75)' }}>
              A Sessão de Liberação e Elevação Vibracional é um atendimento individual de <span className="numerals">60</span> minutos onde trabalhamos juntos o que está travando o seu campo agora. Sem rodeios. Sem generalizações. Com presença total e direcionamento personalizado para a sua demanda.
            </p>
          </motion.div>

          {/* ── O QUE ACONTECE ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            className="rounded-sm p-6 sm:p-8 md:p-10 mb-8"
            style={{
              background: 'rgba(255,255,255,0.025)',
              border: '1px solid rgba(255,255,255,0.07)',
            }}
          >
            <SectionLabel>O que acontece nessa sessão</SectionLabel>
            <ul className="space-y-4">
              {PROCESSO_ITEMS.map((item, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.75 + i * 0.08 }}
                  className="flex items-start gap-3"
                >
                  <span className="flex-shrink-0 mt-0.5" style={{ color: ACCENT, fontSize: '14px', lineHeight: 1.7 }}>
                    ✦
                  </span>
                  <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.8)' }}>
                    <span className="font-medium" style={{ color: 'var(--ink, #e8e0d5)' }}>{item.title}</span>
                    {' — '}{item.desc}
                  </p>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* ── PARA QUEM É ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8 }}
            className="rounded-sm p-6 sm:p-8 md:p-10 mb-8"
            style={{
              background: 'rgba(255,255,255,0.025)',
              border: '1px solid rgba(255,255,255,0.07)',
            }}
          >
            <SectionLabel>Para quem é essa sessão</SectionLabel>
            <div className="space-y-3">
              {[
                'Para quem sente que algo específico está travado e quer clareza e cura agora.',
                'Para quem não quer um processo longo nesse momento mas quer resultado real.',
                'Para quem chegou por indicação ou por intuição e sabe que é hora.',
              ].map((text, i) => (
                <p key={i} className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.75)' }}>
                  {text}
                </p>
              ))}
            </div>
          </motion.div>

          {/* ── O QUE VOCÊ RECEBE ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.88 }}
            className="rounded-sm p-6 sm:p-8 md:p-10 mb-8"
            style={{
              background: 'rgba(255,255,255,0.025)',
              border: '1px solid rgba(255,255,255,0.07)',
            }}
          >
            <SectionLabel>O que você recebe</SectionLabel>
            <ul className="space-y-4">
              {RECEBE_ITEMS.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-0.5" style={{ color: ACCENT, fontSize: '14px', lineHeight: 1.7 }}>
                    ✦
                  </span>
                  <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.8)' }}>
                    {'title' in item && item.title ? (
                      <>
                        <span className="font-medium" style={{ color: 'var(--ink, #e8e0d5)' }}>{item.title}.</span>
                        {' '}{item.desc}
                      </>
                    ) : item.text}
                  </p>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* ── INVESTIMENTO + CTA ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95 }}
            className="rounded-sm p-6 sm:p-8 md:p-10 relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${ACCENT}0a 0%, rgba(255,255,255,0.02) 100%)`,
              border: `1px solid ${ACCENT}33`,
            }}
          >
            {/* Glow */}
            <div
              className="absolute -bottom-16 -left-16 w-48 h-48 pointer-events-none"
              style={{ background: `radial-gradient(closest-side, ${ACCENT}12, transparent)` }}
            />

            <div className="text-center">
              <SectionLabel>Investimento</SectionLabel>

              <p
                className="font-display text-[32px] sm:text-[38px] md:text-[44px] leading-none mb-2"
                style={{ color: 'var(--ink, #e8e0d5)' }}
              >
                <span className="numerals">R$450,00</span>
              </p>
              <p
                className="font-body text-[15px] sm:text-[16px] mb-1"
                style={{ color: `${ACCENT_SOFT}99` }}
              >
                à vista ou <span className="numerals">6x</span> de <span className="numerals">R$84,42</span>
              </p>

              <Divider />

              <p
                className="font-body type-body italic mb-8 max-w-md mx-auto"
                style={{ color: 'rgba(232,224,213,0.6)' }}
              >
                Assim que o pagamento for confirmado entrarei em contato para agendarmos juntos o melhor dia e horário.
              </p>

              <a
                href={SESSAO_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full sm:w-auto sm:inline-block text-center btn-primary px-8 py-5 min-h-[60px] rounded-sm font-body type-button tracking-[0.12em] uppercase transition-all duration-300 active:scale-[0.98]"
              >
                Quero minha sessão agora
              </a>
            </div>
          </motion.div>

        </motion.div>
      </main>

      {/* Footer */}
      <footer
        className="w-full px-4 py-8 text-center border-t mt-12"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <p className="font-display italic text-[15px] mb-2" style={{ color: `${ACCENT_SOFT}99` }}>
          Com amor e presença, Mônica
        </p>
        <p className="text-[11px] tracking-[0.3em] uppercase font-body opacity-40" style={{ color: ACCENT_SOFT }}>
          Reacenda Luz
        </p>
      </footer>
    </div>
  )
}
